import React from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import _get from 'lodash.get';

import Button from 'components/Button';
import DataLayerPush from 'components/DataLayerPush';
import Input from 'components/Input';
import MarketingPermission from 'components/MarketingPermission';
import { fetchValuation, inputChange, resetCarDetails } from 'ducks/valuation';
import {
  getActiveCampaignData,
  sendToDriftrock
} from 'utils/campaignsAndDriftrock';
import {
  sendInputAttemptEvent,
  sendZutoOptInResultEvent,
  sendOemOptInResultEvent,
  sendOemPopupResultEvent,
  sendVitalityPopupResultEvent,
  sendZutoPopupResultEvent
} from 'utils/ga/events';
import request from 'utils/request';
import regex from 'utils/regex';
import { isSmallViewPort } from 'utils/viewports';
import CarAttributes from '../CarAttributes';
import { intentOptions } from '../EnterCarDetailsForm';
import OptInBox from '../OptIn/OptInBox';
import style from './style.scss';

const SALUTATIONS = ['Ms', 'Mrs', 'Miss', 'Mr', 'Dr', 'Mx'];

export class EnterUserDetailsForm extends React.Component {
  static propTypes = {
    carDetails: PropTypes.shape({
      colour: PropTypes.string,
      derivative: PropTypes.string,
      manufacturer: PropTypes.string,
      model: PropTypes.string,
      year: PropTypes.string
    }),
    fetchValuation: PropTypes.func.isRequired,
    formValues: PropTypes.shape({
      salutation: PropTypes.string,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      intent: PropTypes.string.isRequired,
      mileage: PropTypes.string.isRequired,
      phone: PropTypes.string,
      postcode: PropTypes.string,
      email: PropTypes.string,
      vrm: PropTypes.string.isRequired,
      zutoOptIn: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
      campaignOptIn: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
      vitalityOptIn: PropTypes.oneOfType([PropTypes.bool, PropTypes.string])
    }),
    oemDetails: PropTypes.shape({
      displayName: PropTypes.string,
      image: PropTypes.shape({
        src: PropTypes.string,
        alt: PropTypes.string
      }),
      key: PropTypes.string,
      desiredMake: PropTypes.string,
      desiredModel: PropTypes.string
    }),
    history: PropTypes.shape({}).isRequired,
    inputChange: PropTypes.func.isRequired,
    resetCarDetails: PropTypes.func.isRequired
  };

  constructor(props) {
    super(props);
    this.mySteps = React.createRef();
  }

  executeScroll = () => this.mySteps.current.scrollIntoView();

  state = {
    inputsValidity: {},
    isEligibleForZutoOptIn: false,
    campaignDescription: null,
    oemCampaignData: null,
    isEligibleForVitalityOptIn: false
  };

  // Check if we want to display the OEM Opt-in box
  oemOptInActive = true;
  isEligibleForOEMOptIn = intent =>
    this.oemOptInActive &&
    [
      intentOptions.SELLING_BUYING_NEW,
      intentOptions.SELLING_BUYING_USED
    ].includes(intent);

  // Check if we want to display the Zuto Opt-in box
  zutoOptInActive = true;
  isEligibleForZutoOptIn = intent =>
    this.zutoOptInActive &&
    [intentOptions.SELLING_BUYING_USED, intentOptions.BUYING].includes(intent);

  // Check if we want to dispaly the Vitality Opt-in box
  vitalityOptInActive = false;
  isEligibleForVitalityOptIn = intent =>
    this.vitalityOptInActive &&
    [
      intentOptions.SELLING_BUYING_NEW,
      intentOptions.SELLING_BUYING_USED,
      intentOptions.BUYING,
      intentOptions.INSURING
    ].includes(intent);

  async componentDidMount() {
    const {
      formValues: { intent },
      oemDetails: { desiredMake, desiredModel }
    } = this.props;

    if (this.isEligibleForZutoOptIn(intent)) {
      this.setState({
        isEligibleForZutoOptIn: this.isEligibleForZutoOptIn(intent)
      });
      sendZutoPopupResultEvent();
    }

    if (this.isEligibleForOEMOptIn(intent)) {
      const oemCampaignData = await getActiveCampaignData(
        desiredMake,
        desiredModel
      );
      this.setState({ oemCampaignData });
      oemCampaignData && sendOemPopupResultEvent(desiredMake);
    }

    if (this.isEligibleForVitalityOptIn(intent)) {
      this.setState({
        isEligibleForVitalityOptIn: this.isEligibleForVitalityOptIn(intent)
      });
      sendVitalityPopupResultEvent();
    }
  }

  handleChange = event => {
    const { inputChange } = this.props;
    const target = event.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const name = target.name;

    inputChange(name, value);

    // removes red label and input border when input changes
    this.setState(prevState => {
      const updatedInputsValidity = {
        ...prevState.inputsValidity,
        [name]: undefined
      };
      return { inputsValidity: updatedInputsValidity };
    });
  };

  optInValueChanged = (field, value) => {
    this.props.inputChange(field, value);
    this.setState(currentState => ({
      inputsValidity: {
        ...currentState.inputsValidity,
        [field]: this.validateField(field, value)
      }
    }));
  };

  sendGTMEvent = event => {
    event.preventDefault();
    const {
      target: { name }
    } = event;
    sendInputAttemptEvent(name);
  };

  handleSubmit = async event => {
    event.preventDefault();
    this.setState({ inputsValidity: {} });

    const {
      history,
      formValues: {
        postcode,
        zutoOptIn,
        campaignOptIn,
        desiredMake,
        desiredModel,
        intent,
        vitalityOptIn
      },
      carDetails: { manufacturer, model }
    } = this.props;

    const isFormValid = this.checkFormValid();

    if (isFormValid) {
      try {
        const axiosRequest = await request();
        const response = await axiosRequest.get(
          `/postcode?postcode=${postcode}`
        );
        if (response.data && response.data.results) {
          if (intent === intentOptions.BUYING) {
            sendZutoOptInResultEvent(zutoOptIn, manufacturer, model);
          } else if (intent === intentOptions.SELLING_BUYING_USED) {
            sendZutoOptInResultEvent(zutoOptIn, desiredMake, desiredModel);
          }

          if (this.isEligibleForOEMOptIn(intent)) {
            sendOemOptInResultEvent(campaignOptIn, desiredMake, desiredModel);
          }

          this.props.fetchValuation();
          if (campaignOptIn || zutoOptIn || vitalityOptIn) {
            const { oemCampaignData } = this.state;
            sendToDriftrock(oemCampaignData);
          }

          this.setState({ inputsValidity: {} });
          history.push('/car-valuation/valuation');
        } else if (response.data && response.data.results === false) {
          this.setState({
            inputsValidity: {
              postcode: {
                name: 'postcode',
                isValid: false,
                msg: 'Please enter a valid UK postcode'
              }
            }
          });
        }
      } catch (e) {
        console.warn('Postcode check failed', e);
      }
    }
  };

  checkFormValid = () => {
    const { formValues } = this.props;
    const validState = {};
    Object.keys(formValues).forEach(key => {
      validState[key] = this.validateField(key, this.props.formValues[key]);
    });

    // Don't validate the 'zutoOptIn' field if we don't send data to Zuto
    if (!this.state.isEligibleForZutoOptIn) {
      delete validState.zutoOptIn;
    }

    // Don't validate the 'campaignOptIn' field if we don't send data to Driftrock
    if (!this.state.oemCampaignData) {
      delete validState.campaignOptIn;
    }

    // Don't validate the 'vitalityOptIn' field if we don't send data to Driftrock
    if (!this.state.isEligibleForVitalityOptIn) {
      delete validState.vitalityOptIn;
    }

    const isFormValid = Object.keys(validState).every(
      fieldName => validState[fieldName].isValid
    );

    if (!isFormValid) {
      this.setState({ inputsValidity: validState }, () => this.executeScroll());
    }

    return isFormValid;
  };

  validateField(key, value) {
    if (key === 'firstName') {
      return {
        name: key,
        isValid:
          regex.name.test(value) &&
          !regex.noEmojies.test(value) &&
          regex.required.test(value),
        msg: value
          ? 'Please enter a first name without numbers or special characters.'
          : 'Please enter your first name'
      };
    } else if (key === 'lastName') {
      return {
        name: key,
        isValid:
          regex.name.test(value) &&
          !regex.noEmojies.test(value) &&
          regex.required.test(value),
        msg: value
          ? 'Please enter a last name without numbers or special characters.'
          : 'Please enter your last name'
      };
    } else if (key === 'salutation') {
      return {
        name: key,
        isValid: regex.required.test(value),
        msg: 'Please select a title'
      };
    } else if (key === 'email') {
      return {
        name: key,
        isValid: regex.email.test(value) && regex.required.test(value),
        msg: value
          ? 'Please enter a valid email address'
          : 'Please enter your email address'
      };
    } else if (key === 'postcode') {
      return {
        name: key,
        isValid: regex.required.test(value),
        msg: 'Please enter your postcode'
      };
    } else if (key === 'phone') {
      return {
        name: key,
        isValid: regex.phone.test(value),
        msg: value
          ? 'Please enter a valid phone number'
          : 'Please enter your phone number'
      };
    } else if (key === 'zutoOptIn') {
      return {
        name: key,
        isValid: typeof value === 'boolean',
        msg: 'Please select an option'
      };
    } else if (key === 'campaignOptIn') {
      return {
        name: key,
        isValid: typeof value === 'boolean',
        msg: 'Please select an option'
      };
    } else {
      return { name: key, isValid: true };
    }
  }

  handleResetCarDetails = e => {
    e.preventDefault();
    const { history, resetCarDetails } = this.props;
    resetCarDetails();
    history.push('/car-valuation');
  };

  render() {
    const {
      carDetails: { colour, derivative, manufacturer, model, year },
      formValues: {
        salutation,
        firstName,
        lastName,
        mileage,
        phone,
        email,
        postcode,
        vrm,
        zutoOptIn,
        campaignOptIn,
        intent,
        vitalityOptIn
      },
      oemDetails
    } = this.props;

    const {
      inputsValidity,
      isEligibleForZutoOptIn,
      oemCampaignData,
      isEligibleForVitalityOptIn
    } = this.state;

    const dataLayerData = {
      content_type: 'valuations',
      navigation_section: 'step 2',
      make: manufacturer,
      model: model
    };
    const car = `${manufacturer} ${model} ${derivative}`;

    const optInData = {};
    optInData.zuto = isEligibleForZutoOptIn
      ? {
          error:
            inputsValidity.zutoOptIn &&
            !inputsValidity.zutoOptIn.isValid &&
            inputsValidity.zutoOptIn.msg,
          handleValueChange: value =>
            this.optInValueChanged('zutoOptIn', value),
          isOptedIn: zutoOptIn
        }
      : undefined;
    optInData.oem = oemCampaignData
      ? {
          error:
            inputsValidity.campaignOptIn &&
            !inputsValidity.campaignOptIn.isValid &&
            inputsValidity.campaignOptIn.msg,
          handleValueChange: value =>
            this.optInValueChanged('campaignOptIn', value),
          isOptedIn: campaignOptIn,
          image:
            oemDetails.image &&
            oemDetails.image.src.replace(
              'https://media.whatcar.com',
              'https://media.whatcar.com/fit-in/100x200'
            ),
          displayName: oemDetails.displayName,
          campaignData: oemCampaignData
        }
      : undefined;
    optInData.vitality = isEligibleForVitalityOptIn
      ? {
          error:
            inputsValidity.vitalityOptIn &&
            !inputsValidity.vitalityOptIn.isValid &&
            inputsValidity.vitalityOptIn.msg,
          handleValueChange: value =>
            this.optInValueChanged('vitalityOptIn', value),
          isOptedIn: vitalityOptIn
        }
      : undefined;

    return mileage ? (
      <div className={style.row}>
        <div className={classNames(style.col, style.formContainer)}>
          <h2 className={style.stepHeading}>About you</h2>
          <div className={style.stepImage}>
            <div className={style.lineContainer}>
              <div className={style.blueLine} />
              <div className={style.greyLine} />
            </div>
            <div className={style.steps} ref={this.mySteps}>
              <div className={style.stepDone} />
              <div data-cy="step2" className={style.stepActive}>
                2
              </div>
              <div>3</div>
            </div>
          </div>
          <form onSubmit={this.handleSubmit}>
            <div className={style.carDetails}>
              <CarAttributes
                car={car}
                colour={colour}
                year={year}
                vrm={vrm}
                mileage={mileage}
              />
              <div className={style.carChangeLink}>
                <a href="#" onClick={this.handleResetCarDetails}>
                  This is not the correct car
                </a>
              </div>
            </div>
            <div className={style.nameFieldsContainer}>
              <Input
                className={classNames(style.input, style.salutation)}
                placeholder="Select title"
                name="salutation"
                type="select"
                options={SALUTATIONS}
                value={salutation}
                onChange={this.handleChange}
                onBlur={this.sendGTMEvent}
                isInvalid={
                  inputsValidity.salutation &&
                  !inputsValidity.salutation.isValid
                }
                errorMessage={
                  inputsValidity.salutation && inputsValidity.salutation.msg
                }
                label={<span>Title</span>}
              />
              <Input
                errorMessage={
                  inputsValidity.firstName && inputsValidity.firstName.msg
                }
                isInvalid={
                  inputsValidity.firstName && !inputsValidity.firstName.isValid
                }
                label={<span>First name</span>}
                name="firstName"
                onChange={this.handleChange}
                onBlur={this.sendGTMEvent}
                placeholder="Enter your first name"
                value={firstName}
                className={style.input}
              />
              <Input
                errorMessage={
                  inputsValidity.lastName && inputsValidity.lastName.msg
                }
                isInvalid={
                  inputsValidity.lastName && !inputsValidity.lastName.isValid
                }
                label={<span>Last name</span>}
                name="lastName"
                onChange={this.handleChange}
                onBlur={this.sendGTMEvent}
                placeholder="Enter your last name"
                value={lastName}
                className={style.input}
              />
            </div>
            <Input
              errorMessage={inputsValidity.email && inputsValidity.email.msg}
              isInvalid={inputsValidity.email && !inputsValidity.email.isValid}
              label={<span>Email</span>}
              name="email"
              onChange={this.handleChange}
              onBlur={this.sendGTMEvent}
              placeholder={
                isSmallViewPort()
                  ? 'You will receive a valuation'
                  : 'A valuation certificate will be emailed to you'
              }
              value={email}
              className={style.input}
            />
            <Input
              errorMessage={inputsValidity.phone && inputsValidity.phone.msg}
              isInvalid={inputsValidity.phone && !inputsValidity.phone.isValid}
              label={<span>Phone number</span>}
              name="phone"
              onChange={this.handleChange}
              onBlur={this.sendGTMEvent}
              placeholder="Enter your phone number"
              value={phone}
              className={style.input}
            />
            <Input
              label={<span>Postcode</span>}
              value={postcode}
              name="postcode"
              placeholder="Enter your postcode"
              onChange={this.handleChange}
              onBlur={this.sendGTMEvent}
              isInvalid={
                inputsValidity.postcode && !inputsValidity.postcode.isValid
              }
              errorMessage={
                inputsValidity.postcode && inputsValidity.postcode.msg
              }
              className={style.input}
            />
            <MarketingPermission onChange={this.handleChange} />
            <OptInBox optInData={optInData} />
            <Button
              data-cy="submit"
              text="Get a free valuation"
              onClick={this.handleSubmit}
            />
          </form>
          <p className={style.partners}>
            What Car? Valuations data partners: Auto Trader
          </p>
        </div>
        {dataLayerData && (
          <DataLayerPush event={'view_page'} {...dataLayerData} />
        )}
      </div>
    ) : (
      <Redirect to="/car-valuation" />
    );
  }
}

const mapStateToProps = state => ({
  carDetails: {
    colour: state.valuation.carDetails.colour,
    derivative: state.valuation.carDetails.derivative,
    manufacturer: state.valuation.carDetails.manufacturer,
    model: state.valuation.carDetails.model,
    year: state.valuation.carDetails.year
  },
  formValues: {
    salutation: state.valuation.formValues.salutation,
    firstName: state.valuation.formValues.firstName,
    lastName: state.valuation.formValues.lastName,
    intent: state.valuation.formValues.intent,
    mileage: state.valuation.formValues.mileage,
    postcode: state.valuation.formValues.postcode,
    phone: state.valuation.formValues.phone,
    email: state.valuation.formValues.email,
    vrm: state.valuation.formValues.vrm,
    zutoOptIn: state.valuation.formValues.zutoOptIn,
    campaignOptIn: state.valuation.formValues.campaignOptIn,
    vitalityOptIn: state.valuation.formValues.vitalityOptIn,
    desiredMake: state.valuation.formValues.desiredMake,
    desiredModel: state.valuation.formValues.desiredModel
  },
  oemDetails: {
    displayName: state.valuation.oemDetails.displayName,
    image: state.valuation.oemDetails.image,
    key: state.valuation.oemDetails.key,
    desiredMake: state.valuation.oemDetails.desiredMake,
    desiredModel: state.valuation.oemDetails.desiredModel
  }
});

const mapDispatchToProps = {
  fetchValuation,
  inputChange,
  resetCarDetails
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(EnterUserDetailsForm);



// WEBPACK FOOTER //
// ./src/components/EnterUserDetailsForm/index.js